import type {
    Organization,
    WebSite,
    ContactPoint,
    SearchAction,
    Thing,
    WithContext,
    OpeningHoursSpecification,
} from 'schema-dts';
import { PartialDeep } from 'type-fest';

import { LoggingConfig, updateLoggerConfig } from '../utils/logger';

export type NonStringStructure<T extends Thing> = T extends string ? never : T;

export interface StructuredDataConfig {
    storyblocks: {
        baseUrl: string;
        organizationId: string;
        logo: {
            url: string;
            width: string;
            height: string;
        };
        social: {
            facebook: string;
            twitter: string;
            linkedin: string;
            youtube: string;
            instagram: string;
        };
        endpoints: {
            pricing: string;
            support: string;
            video: string;
        };
        contact: {
            email: string;
            phone: string;
            supportPhone: string;
            salesPhone: string;
            faxNumber?: string;
            contactType: string;
            areaServed: string;
            availableLanguages: string[];
            hoursAvailable: OpeningHoursSpecification;
        };
        website: {
            searchUrl: string;
            searchQueryParam: string;
        };
        legal: {
            privacyPolicyUrl: string;
            termsOfServiceUrl: string;
            copyrightYear: number;
            copyrightHolder: string;
            jurisdiction: string;
            licenseName?: string;
            licenseUrl?: string;
        };
        navigation: {
            mainMenuItems: Array<{
                name: string;
                url: string;
                position: number;
            }>;
            footerLinks: Array<{
                name: string;
                url: string;
                section: string;
            }>;
        };
    };
    cache: {
        maxVideoEntries: number;
        maxDurationEntries: number;
        ttl?: number;
    };
    validation: {
        maxRating: number;
        minRating: number;
        currencyCodeLength: number;
    };
    logging: {
        enabled: boolean;
        level: 'debug' | 'info' | 'warn' | 'error';
    };
}

export const defaultConfig: StructuredDataConfig = {
    storyblocks: {
        baseUrl: 'https://www.storyblocks.com',
        organizationId: 'https://www.storyblocks.com/#organization',
        logo: {
            url: 'https://cdn.builder.io/api/v1/image/assets%2Fe7fb3d1e4da14573bd2a1edb7bfee5f1%2F8977452afd404719a1cfac9521c44586',
            width: '200px',
            height: '50px',
        },
        social: {
            facebook: 'https://www.facebook.com/storyblocks/',
            twitter: 'https://twitter.com/StoryblocksCo',
            linkedin: 'https://www.linkedin.com/company/storyblocks/',
            youtube: 'https://www.youtube.com/channel/UCrxwwZTQOPCTUVlHsjNU7Fw',
            instagram: 'https://www.instagram.com/StoryblocksCo/',
        },
        endpoints: {
            pricing: '/pricing',
            support: '/support',
            video: '/video',
        },
        contact: {
            email: 'support@storyblocks.com',
            phone: '+1-800-555-0123',
            supportPhone: '+1-800-SUPPORT',
            salesPhone: '+1-800-SALES-01',
            faxNumber: undefined,
            contactType: 'Customer Service',
            areaServed: 'Worldwide',
            availableLanguages: ['English', 'Spanish'],
            hoursAvailable: {
                '@type': 'OpeningHoursSpecification',
                opens: '09:00',
                closes: '17:00',
                dayOfWeek: [
                    'https://schema.org/Monday',
                    'https://schema.org/Tuesday',
                    'https://schema.org/Wednesday',
                    'https://schema.org/Thursday',
                    'https://schema.org/Friday',
                ],
            },
        },
        website: {
            searchUrl: '/search',
            searchQueryParam: 'q',
        },
        legal: {
            privacyPolicyUrl: '/privacy-policy',
            termsOfServiceUrl: '/terms-of-service',
            copyrightYear: new Date().getFullYear(),
            copyrightHolder: 'Storyblocks',
            jurisdiction: 'United States',
            licenseName: undefined,
            licenseUrl: undefined,
        },
        navigation: {
            mainMenuItems: [
                { name: 'Home', url: '/', position: 1 },
                { name: 'Video', url: '/video', position: 2 },
                { name: 'Audio', url: '/audio', position: 3 },
                { name: 'Images', url: '/images', position: 4 },
                { name: 'Pricing', url: '/pricing', position: 5 },
            ],
            footerLinks: [
                { name: 'About Us', url: '/about', section: 'company' },
                { name: 'Careers', url: '/careers', section: 'company' },
                { name: 'Press', url: '/press', section: 'company' },
                { name: 'Privacy Policy', url: '/privacy-policy', section: 'legal' },
                { name: 'Terms of Service', url: '/terms-of-service', section: 'legal' },
                { name: 'Cookie Policy', url: '/cookie-policy', section: 'legal' },
                { name: 'Help Center', url: '/support', section: 'support' },
                { name: 'Contact Us', url: '/contact', section: 'support' },
            ],
        },
    },
    cache: {
        maxVideoEntries: 1000,
        maxDurationEntries: 500,
        ttl: undefined,
    },
    validation: {
        maxRating: 5,
        minRating: 1,
        currencyCodeLength: 3,
    },
    logging: {
        enabled: false,
        level: 'info',
    },
};

class ConfigManager {
    private _config: StructuredDataConfig;
    private configProxy: StructuredDataConfig;

    constructor() {
        this._config = { ...defaultConfig };

        // Create reactive proxy that watches for logging config changes
        this.configProxy = this.createReactiveConfig(this._config);

        // Initialize logger with config on startup
        updateLoggerConfig(this._config.logging);
    }

    private createReactiveConfig(config: StructuredDataConfig): StructuredDataConfig {
        return new Proxy(config, {
            set(target: StructuredDataConfig, property: string | symbol, value: unknown) {
                const result = Reflect.set(target, property, value);

                // If logging config changed, update logger
                if (property === 'logging' && value !== target[property]) {
                    updateLoggerConfig(value as LoggingConfig);
                }

                return result;
            },
            get(target: StructuredDataConfig, property: string | symbol) {
                const value = Reflect.get(target, property) as unknown;

                // If getting logging object, wrap it in proxy too
                if (property === 'logging' && typeof value === 'object' && value !== null) {
                    return new Proxy(value, {
                        set(loggingTarget: object, loggingProperty: string | symbol, loggingValue: LoggingConfig) {
                            const result = Reflect.set(loggingTarget, loggingProperty, loggingValue);
                            // Any change to logging properties updates logger
                            updateLoggerConfig(loggingValue);
                            return result;
                        },
                    });
                }

                return value;
            },
        });
    }

    getConfig(): Readonly<StructuredDataConfig> {
        // Return the proxy wrapped in a readonly interface, but the proxy itself is still mutable
        return this.configProxy as Readonly<StructuredDataConfig>;
    }

    updateConfig(updates: PartialDeep<StructuredDataConfig>): void {
        this._config = this.deepMerge(this._config, updates as Record<string, unknown>);
        // Recreate proxy with updated config
        this.configProxy = this.createReactiveConfig(this._config);
        // Update logger config if logging config was changed
        if (updates.logging) {
            updateLoggerConfig(this._config.logging);
        }
    }

    resetConfig(): void {
        this._config = { ...defaultConfig };
        // Recreate proxy with new config
        this.configProxy = this.createReactiveConfig(this._config);
        // Reset logger config as well
        updateLoggerConfig(this._config.logging);
    }

    getOrganizationSchema(): NonStringStructure<Organization> {
        const { storyblocks } = this._config;
        return {
            '@type': 'Organization',
            '@id': storyblocks.organizationId,
            name: 'Storyblocks',
            url: storyblocks.baseUrl,
            logo: {
                '@type': 'ImageObject',
                url: storyblocks.logo.url,
                width: storyblocks.logo.width,
                height: storyblocks.logo.height,
            },
            sameAs: Object.values(storyblocks.social),
        };
    }

    getWebsiteSchema(): NonStringStructure<WithContext<WebSite>> {
        const { storyblocks } = this._config;

        const potentialAction: SearchAction = {
            '@type': 'SearchAction',
            target: {
                '@type': 'EntryPoint',
                urlTemplate: `${storyblocks.baseUrl}${storyblocks.website.searchUrl}?${storyblocks.website.searchQueryParam}={search_term_string}`,
            },
            query: 'required name=search_term_string',
        };

        return {
            '@type': 'WebSite',
            '@context': 'https://schema.org',
            name: 'Storyblocks',
            url: storyblocks.baseUrl,
            publisher: this.getOrganizationSchema(),
            potentialAction,
            sameAs: Object.values(storyblocks.social).filter(Boolean),
        };
    }

    getEnhancedWebsiteSchema(): WebSite {
        const { storyblocks } = this._config;
        const searchAction: SearchAction = {
            '@type': 'SearchAction',
            target: {
                '@type': 'EntryPoint',
                urlTemplate: `${storyblocks.baseUrl}${storyblocks.website.searchUrl}?${storyblocks.website.searchQueryParam}={search_term_string}`,
            },
            query: 'required name=search_term_string',
        };

        return {
            '@type': 'WebSite',
            '@id': `${storyblocks.baseUrl}/#website`,
            url: storyblocks.baseUrl,
            name: 'Storyblocks',
            publisher: this.getOrganizationSchema(),
            potentialAction: searchAction,
        };
    }

    getContactPointSchema(): ContactPoint {
        const { storyblocks } = this._config;
        return {
            '@type': 'ContactPoint',
            email: storyblocks.contact.email,
            telephone: storyblocks.contact.phone,
            contactType: storyblocks.contact.contactType,
            areaServed: storyblocks.contact.areaServed,
            availableLanguage: storyblocks.contact.availableLanguages,
            hoursAvailable: storyblocks.contact.hoursAvailable,
        };
    }

    getPublisherReference(): string {
        return this._config.storyblocks.organizationId;
    }

    buildUrl(path: string): string {
        const { baseUrl } = this._config.storyblocks;
        const cleanBase = baseUrl.replace(/\/$/, '');
        const cleanPath = path.startsWith('/') ? path : `/${path}`;
        return `${cleanBase}${cleanPath}`;
    }

    private deepMerge(target: StructuredDataConfig, source: Record<string, unknown>): StructuredDataConfig {
        const merged = {
            ...target,
            ...source,
            cache: source.cache ? { ...target.cache, ...source.cache } : target.cache,
            validation: source.validation ? { ...target.validation, ...source.validation } : target.validation,
            logging: source.logging ? { ...target.logging, ...source.logging } : target.logging,
        } as StructuredDataConfig;

        if (source.storyblocks) {
            const sourceStoryblocks = source.storyblocks as Record<string, unknown>;
            merged.storyblocks = {
                ...target.storyblocks,
                ...sourceStoryblocks,
                logo: sourceStoryblocks.logo
                    ? { ...target.storyblocks.logo, ...sourceStoryblocks.logo }
                    : target.storyblocks.logo,
                social: sourceStoryblocks.social
                    ? { ...target.storyblocks.social, ...sourceStoryblocks.social }
                    : target.storyblocks.social,
                endpoints: sourceStoryblocks.endpoints
                    ? { ...target.storyblocks.endpoints, ...sourceStoryblocks.endpoints }
                    : target.storyblocks.endpoints,
                contact: sourceStoryblocks.contact
                    ? { ...target.storyblocks.contact, ...sourceStoryblocks.contact }
                    : target.storyblocks.contact,
                website: sourceStoryblocks.website
                    ? { ...target.storyblocks.website, ...sourceStoryblocks.website }
                    : target.storyblocks.website,
                legal: sourceStoryblocks.legal
                    ? { ...target.storyblocks.legal, ...sourceStoryblocks.legal }
                    : target.storyblocks.legal,
                navigation: sourceStoryblocks.navigation
                    ? { ...target.storyblocks.navigation, ...sourceStoryblocks.navigation }
                    : target.storyblocks.navigation,
            } as typeof target.storyblocks;
        }

        return merged;
    }
}

export const config = new ConfigManager();

export const getConfig = (): Readonly<StructuredDataConfig> => config.getConfig();
export const updateConfig = (updates: PartialDeep<StructuredDataConfig>): void => config.updateConfig(updates);
export const resetConfig = (): void => config.resetConfig();
export const buildUrl = (path: string): string => config.buildUrl(path);
